import type { ComponentChildren, JSX } from "preact";
import { useState } from "preact/hooks";

/**
 * 컨트롤 프리미티브.
 *
 * 여기 모은 이유: 버튼이 헤더·메뉴·서랍 세 곳에 흩어져 있었고, 각자 `class="btn"` 에
 * 살을 붙이다 보니 같은 동작인데 생김새가 조금씩 달라졌다. 형태(반경·테두리·자간·전이)는
 * 한 곳에서 정하고, **변형은 색만 바꾼다** — 그래야 "헤더 버튼의 느낌"이 메뉴와 삭제
 * 버튼까지 그대로 간다.
 *
 * 색 규칙: 평소엔 무채색, hover 에서만 의미색(연두 / 위험은 빨강)이 올라온다.
 */

type Tone = "plain" | "danger";

/**
 * `IntrinsicElements["button"]` 을 쓴다 — Preact 의 `HTMLAttributes` 에는 `disabled` 처럼
 * 요소별 속성이 없어서, 그걸로 타이핑하면 `disabled` 를 넘기는 순간 컴파일이 막힌다.
 */
type NativeButton = Omit<JSX.IntrinsicElements["button"], "class" | "children">;

export function Button({
  tone = "plain",
  small,
  children,
  ...rest
}: {
  tone?: Tone;
  small?: boolean;
  children: ComponentChildren;
} & NativeButton) {
  return (
    <button class={["btn", tone === "danger" ? "danger" : "", small ? "sm" : ""].join(" ")} {...rest}>
      {children}
    </button>
  );
}

/**
 * 잠김 표시. 폰트 이모지(🔒)나 문자(—)를 쓰지 않는 이유는 같다: OS·폰트마다 다르게 그려지고
 * 크기·색을 맞출 수 없다. SVG 는 `currentColor` 를 따라가므로 주변 글자색과 항상 맞는다.
 */
export function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
      <rect x="3.4" y="7" width="9.2" height="6.4" rx="1.6" fill="none" stroke="currentColor" stroke-width="1.3" />
      <path d="M5.7 7V5.3a2.3 2.3 0 0 1 4.6 0V7" fill="none" stroke="currentColor" stroke-width="1.3" />
    </svg>
  );
}

/** 행 끝의 `⋯`. 아이콘을 SVG 로 두는 이유는 폰트·OS 마다 말줄임표 글리프가 제각각이라서다. */
export function DotsButton(props: NativeButton) {
  return (
    <button class="btn icon" aria-label="작업" {...props}>
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
        <circle cx="3" cy="8" r="1.35" />
        <circle cx="8" cy="8" r="1.35" />
        <circle cx="13" cy="8" r="1.35" />
      </svg>
    </button>
  );
}

/**
 * 도움말 툴팁.
 *
 * 카드 아래 설명 문장을 여기로 옮겼다. 화면에 상시 떠 있으면 밀도가 목적인 대시보드에서
 * 수치보다 글이 많아지고, 그 글은 두 번째 볼 때부터는 아무도 읽지 않는다. 필요할 때만
 * 꺼내 보는 게 맞다.
 *
 * 네이티브 `title` 을 쓰지 않는 이유: 뜨는 데 1초 넘게 걸리고, 색·글꼴·위치를 하나도 정할
 * 수 없어서 화면의 색 규칙과 따로 논다.
 *
 * 키보드로도 열려야 해서 트리거는 `button` 이다 (`:focus-visible` 에서도 뜬다).
 *
 * 펼침 방향은 **재어서 정한다.** 카드가 화면 폭에 따라 3~4열로 접히므로 "오른쪽 끝 카드"가
 * 고정이 아니고, 방향을 prop 으로 손수 지정하면 폭이 바뀌는 순간 말풍선이 화면 밖으로
 * 나간다 (처음에 그렇게 만들었다가 실제로 잘렸다).
 */
export function Tooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const [flip, setFlip] = useState(false);
  // 트리거 좌표는 이벤트가 들고 있다 — ref 를 쓰면 Preact 가 함수 컴포넌트의 ref 를
  // 넘기지 않아 null 이 된다 (RowMenu 에서 겪은 것과 같은 함정).
  const show = (e: { currentTarget: EventTarget | null }) => {
    const el = e.currentTarget as HTMLElement | null;
    if (el) setFlip(window.innerWidth - el.getBoundingClientRect().right < 300);
    setOpen(true);
  };
  return (
    <span class="tipwrap">
      <button
        type="button"
        class="tip-btn"
        aria-label="설명"
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        onFocus={show}
        onBlur={() => setOpen(false)}
        onClick={(e) => (open ? setOpen(false) : show(e))}
      >
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
          <circle cx="8" cy="8" r="6.4" fill="none" stroke="currentColor" stroke-width="1.3" />
          <path
            d="M6.3 6.1a1.75 1.75 0 1 1 2.2 1.7c-.4.13-.5.4-.5.72v.45"
            fill="none"
            stroke="currentColor"
            stroke-width="1.3"
            stroke-linecap="round"
          />
          <circle cx="8" cy="11.4" r="0.85" fill="currentColor" />
        </svg>
      </button>
      {open ? (
        <span class={flip ? "tip tip-left" : "tip"} role="tooltip">
          {text}
        </span>
      ) : null}
    </span>
  );
}

/**
 * 체크박스. 네이티브 `input` 은 OS 테마를 그대로 끌고 와서(윈도우에선 파란 사각형) 화면의
 * 색 규칙을 깬다. 그래서 입력은 접근성용으로 남기고(포커스·키보드·라벨 클릭이 공짜로
 * 따라온다) **보이는 상자는 우리가 그린다.**
 */
export function Checkbox({
  checked,
  onToggle,
  children,
}: {
  checked: boolean;
  onToggle: (next: boolean) => void;
  children: ComponentChildren;
}) {
  return (
    <label class="check">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onToggle((e.target as HTMLInputElement).checked)}
      />
      <span class="box" aria-hidden="true">
        <svg viewBox="0 0 12 12" width="12" height="12">
          <path d="M2.5 6.4l2.2 2.2 4.8-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
      <span class="ctext">{children}</span>
    </label>
  );
}
